import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import { catalogApi, catalogKeys } from '../../lib'

/**
 * Hook to fetch auto-related products for a given product SKU
 * First fetches the related SKUs, then fetches the full product data
 *
 * IMPORTANT: useMemo is REQUIRED here because this hook's return value is used
 * directly as Provider context value in RelatedProductsProvider. Without useMemo,
 * every render creates a new object reference, causing all context consumers to re-render.
 */
export function useAutoRelated(productSku: string) {
  // Step 1: Fetch related SKUs
  const skusQuery = useQuery({
    queryKey: catalogKeys.autoRelatedSkus(productSku),
    queryFn: async () => {
      const data = await catalogApi.getAutoRelatedSkus(productSku)
      return data.getAutoRelatedSkus?.map((product) => product.sku) || []
    },
    enabled: !!productSku,
  })

  const skus = skusQuery.data || []

  // Step 2: Fetch products by SKUs
  const productsQuery = useQuery({
    queryKey: [...catalogKeys.autoRelated(productSku), skus],
    queryFn: async () => {
      const data = await catalogApi.getAutoRelatedProducts(skus)
      return data.products.items || []
    },
    enabled: skus.length > 0,
  })

  return useMemo(
    () => ({
      autoRelatedProducts: productsQuery.data || [],
      loading: skusQuery.isLoading || productsQuery.isLoading,
      error: skusQuery.error || productsQuery.error,
    }),
    [productsQuery.data, productsQuery.error, productsQuery.isLoading, skusQuery.error, skusQuery.isLoading]
  )
}
